import { useEffect, useMemo, useState } from "react"
import { Mail, Loader2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Button } from "shared/ui/button"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { toast } from "sonner"
import { getEmailConfig, upsertEmailConfig, type EmailConfigDto } from "modules/admin/api/emailConfigService"

type EmailFormState = {
  environmentCode: string
  smtpHost: string
  smtpPort: string
  senderEmail: string
  dnsBaseUrl: string
}

const ENV_OPTIONS = [
  { value: "DEV", label: "DEV (Development)" },
  { value: "QAS", label: "QAS (Staging)" },
  { value: "PRD", label: "PRD (Production)" },
]

const resolveDefaultEnvironment = () => {
  const raw = (process.env.REACT_APP_ASPNETCORE_ENVIRONMENT ?? "").trim()
  const normalized = raw.toLowerCase()
  if (!normalized) return "DEV"
  if (normalized.includes("prod")) return "PRD"
  if (normalized.includes("qas") || normalized.includes("stage")) return "QAS"
  if (normalized.includes("dev")) return "DEV"
  return raw.toUpperCase()
}

const initialForm = (environmentCode: string): EmailFormState => ({
  environmentCode,
  smtpHost: "",
  smtpPort: "25",
  senderEmail: "",
  dnsBaseUrl: "",
})

export function EmailConfiguration() {
  const defaultEnv = useMemo(resolveDefaultEnvironment, [])
  const [environmentCode, setEnvironmentCode] = useState(defaultEnv)
  const [form, setForm] = useState<EmailFormState>(() => initialForm(defaultEnv))
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [configId, setConfigId] = useState<string | null>(null)

  const loadConfig = async (envCode: string) => {
    setIsLoading(true)
    try {
      const config = await getEmailConfig(envCode)
      if (config) {
        setConfigId(config.emailConfigId)
        setForm({
          environmentCode: config.environmentCode,
          smtpHost: config.smtpHost ?? "",
          smtpPort: String(config.smtpPort ?? 25),
          senderEmail: config.senderEmail ?? "",
          dnsBaseUrl: config.dnsBaseUrl ?? "",
        })
      } else {
        setConfigId(null)
        setForm(initialForm(envCode))
      }
    } catch (error) {
      console.error(error)
      toast.error("โหลดค่า Email Configuration ไม่สำเร็จ")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadConfig(environmentCode)
  }, [environmentCode])

  const updateField = (patch: Partial<EmailFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!form.smtpHost.trim()) {
      toast.error("กรุณากรอก SMTP Host")
      return
    }
    if (!form.senderEmail.trim()) {
      toast.error("กรุณากรอก Sender Email")
      return
    }
    const port = Number.parseInt(form.smtpPort, 10)
    if (Number.isNaN(port) || port <= 0) {
      toast.error("Port ต้องเป็นตัวเลขที่ถูกต้อง")
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        environmentCode: form.environmentCode.trim().toUpperCase(),
        smtpHost: form.smtpHost.trim(),
        smtpPort: port,
        senderEmail: form.senderEmail.trim(),
        dnsBaseUrl: form.dnsBaseUrl.trim() || null,
      }
      const saved = await upsertEmailConfig(payload)
      setConfigId(saved.emailConfigId)
      setForm({
        environmentCode: saved.environmentCode,
        smtpHost: saved.smtpHost,
        smtpPort: String(saved.smtpPort),
        senderEmail: saved.senderEmail,
        dnsBaseUrl: saved.dnsBaseUrl ?? "",
      })
      toast.success("บันทึก Email Configuration สำเร็จ")
    } catch (error) {
      console.error(error)
      toast.error("บันทึก Email Configuration ไม่สำเร็จ")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Mail className="h-5 w-5 text-orange-600" />
            Email Configuration
          </CardTitle>
          <p className="text-sm text-gray-500">
            ตั้งค่าการส่งอีเมลของระบบ แยกตาม environment (DEV/QAS/PRD)
          </p>
        </div>
        <Button onClick={handleSave} disabled={isSaving || isLoading} className="gap-2">
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Environment</Label>
            <Select
              value={environmentCode}
              onValueChange={(value) => {
                setEnvironmentCode(value)
                updateField({ environmentCode: value })
              }}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="เลือก Environment" />
              </SelectTrigger>
              <SelectContent>
                {ENV_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>SMTP Host *</Label>
            <Input
              value={form.smtpHost}
              onChange={(event) => updateField({ smtpHost: event.target.value })}
              placeholder="เช่น irpcry-hub.irpc.co.th"
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label>Port *</Label>
            <Input
              value={form.smtpPort}
              onChange={(event) => updateField({ smtpPort: event.target.value })}
              placeholder="25"
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label>Sender Email *</Label>
            <Input
              value={form.senderEmail}
              onChange={(event) => updateField({ senderEmail: event.target.value })}
              placeholder="eptw@irpc.co.th"
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>DNS / Base URL</Label>
            <Input
              value={form.dnsBaseUrl}
              onChange={(event) => updateField({ dnsBaseUrl: event.target.value })}
              placeholder="https://epermit.irpc.co.th/"
              disabled={isLoading}
            />
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Environment ปัจจุบัน: {environmentCode} {configId ? `(config: ${configId})` : "(ยังไม่มีในระบบ)"}
        </p>
      </CardContent>
    </Card>
  )
}

export default EmailConfiguration
